import type { BookPhotoV1, LibraryBookProgress, StudioSnapshot } from "@lifescapsule/contracts";
import { apiRequest } from "../../shared/api";
import {
  loadCanonicalFamilySnapshot,
  type CanonicalFamilyActivitySummary,
  type CanonicalFamilySnapshot,
} from "../collaboration/family-collaboration-api";
import { opensGuided } from "../guided/guided-routing";

export type CustomerBookRole = "OWNER" | "EDITOR" | "CONTRIBUTOR" | "VIEWER";

export type CustomerBookSummary = {
  id: string;
  role: CustomerBookRole;
  /** The book's name as shown: its story's own title when it has one. */
  title: string;
  /** The name the book was saved under; deleting a book is confirmed against it. */
  savedTitle?: string;
  storyTitle?: string;
  storytellerName: string;
  state: string;
  template: { id: string; name: string; palette: { cover: string } };
  reminderTargetRoles?: Array<"OWNER" | "EDITOR" | "CONTRIBUTOR">;
  deletionState?: string;
  deletionScheduledFor?: string;
  example?: boolean;
  testGiftJourney?: true;
  fulfillmentEligible?: boolean;
  workflowKind?: "INTERVIEW" | "PICTURE_BOOK";
  upgradeAvailable?: boolean;
  revision?: number;
  nextAction?: { label: string; path: string };
  /** Where the book has got to and what its reader does next (GET /books, Stage 2 F2). */
  progress?: LibraryBookProgress;
  /** The page this reader left the book at, kept across devices (F7). */
  readingPage?: number;
  printSimulation?: {
    status: "READY" | "ACCEPTED" | "SHIPPED" | "DELIVERED" | "NEEDS_ATTENTION";
    updatedAt?: string;
    nextTransitionAt?: string;
  };
  reminderNextDueAt?: string;
  latestEmail?: { kind: string; state: string; occurredAt?: string };
  createdAt?: string;
  updatedAt?: string;
};

export type CustomerBookPermissions = StudioSnapshot["permissions"];

export type CustomerBookEnhancement = {
  status: "ready" | "partial" | "unavailable";
  progress?: number;
  nextAction?: StudioSnapshot["nextAction"];
  permissions: CustomerBookPermissions;
  phases?: StudioSnapshot["phases"];
  profile?: StudioSnapshot["profile"];
  family?: CanonicalFamilySnapshot;
  photos: BookPhotoV1[];
  unavailable: Array<"studio" | "family" | "media">;
};

/** Books are shown by their story's own title; the saved name is kept for confirmations. */
export function withStoryTitles(books: CustomerBookSummary[]): CustomerBookSummary[] {
  return books.map((book) =>
    book.storyTitle ? { ...book, savedTitle: book.title, title: book.storyTitle } : book,
  );
}

export function readableBookStatus(value: string) {
  return value
    .toLocaleLowerCase("en-US")
    .replaceAll("_", " ")
    .replace(/^./u, (letter) => letter.toLocaleUpperCase("en-US"));
}

export function selectActiveBook(books: CustomerBookSummary[]) {
  return [...books].sort((left, right) => {
    const leftUpdatedAt = Date.parse(left.updatedAt ?? "");
    const rightUpdatedAt = Date.parse(right.updatedAt ?? "");
    if (Number.isFinite(leftUpdatedAt) && !Number.isFinite(rightUpdatedAt)) return -1;
    if (!Number.isFinite(leftUpdatedAt) && Number.isFinite(rightUpdatedAt)) return 1;
    if (Number.isFinite(leftUpdatedAt) && Number.isFinite(rightUpdatedAt)) {
      const dateDifference = rightUpdatedAt - leftUpdatedAt;
      if (dateDifference !== 0) return dateDifference;
    }
    return left.id.localeCompare(right.id);
  })[0];
}

export function safeBookNextAction(book: CustomerBookSummary, candidate = book.nextAction?.path) {
  const bookRoot = `/app/books/${encodeURIComponent(book.id)}/`;
  // Guided books open the guided steps, which hand off to the full studio when needed.
  const guided = opensGuided(book) ? `${bookRoot}guided` : undefined;
  const fallback = guided ?? `${bookRoot}studio/gather`;
  if (!candidate) return fallback;
  try {
    const parsed = new URL(candidate, "https://lifescapsule.invalid");
    if (parsed.origin !== "https://lifescapsule.invalid" || !parsed.pathname.startsWith(bookRoot))
      return fallback;
    if (guided && parsed.pathname.startsWith(`${bookRoot}studio`)) return guided;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallback;
  }
}

export function bookCoverColor(value: string | undefined) {
  if (!value || !/^#[0-9a-f]{6}$/iu.test(value)) return "#0F3B38";
  const channels = [1, 3, 5].map((index) => Number.parseInt(value.slice(index, index + 2), 16));
  const relativeLuminance = (rgb: number[]) =>
    rgb
      .map((channel) => channel / 255)
      .map((channel) => (channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4))
      .reduce((sum, channel, index) => sum + channel * [0.2126, 0.7152, 0.0722][index]!, 0);
  if (1.05 / (relativeLuminance(channels) + 0.05) >= 4.5) return value;
  const accessible = [...channels];
  while (1.05 / (relativeLuminance(accessible) + 0.05) < 4.5) {
    accessible.forEach((channel, index) => {
      accessible[index] = Math.max(0, Math.floor(channel * 0.9));
    });
  }
  return `#${accessible.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;
}

export function defaultPermissions(role: CustomerBookRole): CustomerBookPermissions {
  if (role === "OWNER") return { edit: true, approve: true, managePhotos: true, finish: true };
  if (role === "EDITOR") return { edit: true, approve: true, managePhotos: false, finish: false };
  if (role === "CONTRIBUTOR")
    return { edit: true, approve: false, managePhotos: false, finish: false };
  return { edit: false, approve: false, managePhotos: false, finish: false };
}

function permissionsForRole(
  role: CustomerBookRole,
  serverPermissions?: CustomerBookPermissions,
): CustomerBookPermissions {
  const roleBoundary = defaultPermissions(role);
  if (!serverPermissions) return roleBoundary;
  return {
    edit: roleBoundary.edit && serverPermissions.edit,
    approve: roleBoundary.approve && serverPermissions.approve,
    managePhotos: roleBoundary.managePhotos && serverPermissions.managePhotos,
    finish: roleBoundary.finish && serverPermissions.finish,
  };
}

export async function loadCustomerBookEnhancement(
  book: CustomerBookSummary,
  idToken: string,
): Promise<CustomerBookEnhancement> {
  const [studioResult, familyResult, mediaResult] = await Promise.allSettled([
    apiRequest<{ data: StudioSnapshot }>(`/books/${book.id}/studio`, idToken),
    loadCanonicalFamilySnapshot(book.id, idToken),
    apiRequest<{ data: BookPhotoV1[] }>(`/books/${book.id}/photos?limit=4`, idToken),
  ]);
  const unavailable: CustomerBookEnhancement["unavailable"] = [];
  if (studioResult.status === "rejected") unavailable.push("studio");
  if (familyResult.status === "rejected") unavailable.push("family");
  if (mediaResult.status === "rejected") unavailable.push("media");
  const snapshot = studioResult.status === "fulfilled" ? studioResult.value.data : undefined;
  return {
    status:
      unavailable.length === 0 ? "ready" : unavailable.length === 3 ? "unavailable" : "partial",
    progress: snapshot?.progress,
    nextAction: snapshot?.nextAction,
    permissions: permissionsForRole(book.role, snapshot?.permissions),
    phases: snapshot?.phases,
    profile: snapshot?.profile,
    family: familyResult.status === "fulfilled" ? familyResult.value : undefined,
    photos:
      mediaResult.status === "fulfilled" && Array.isArray(mediaResult.value.data)
        ? mediaResult.value.data.filter(
            (photo) =>
              typeof photo === "object" &&
              photo !== null &&
              "processingState" in photo &&
              typeof photo.processingState === "string",
          )
        : [],
    unavailable,
  };
}

export function contributingFamilyCount(family?: CanonicalFamilySnapshot) {
  return family?.members.items.filter((member) => member.contributionStatus !== "NOT_STARTED")
    .length;
}

export function familyActivityLabel(activity: CanonicalFamilyActivitySummary) {
  const labels: Record<CanonicalFamilyActivitySummary["eventType"], string> = {
    CONSENT_GRANTED: "A family member granted consent",
    CONSENT_REVOKED: "A family member changed consent",
    MEMBER_REMOVED: "Book access was updated",
    MEMBER_ROLE_CHANGED: "A family member’s role changed",
    CONTRIBUTION_STATUS_CHANGED: "A family contribution moved forward",
    INVITATION_REVOKED: "A family invitation was withdrawn",
    PROMPT_ASSIGNED: "A memory prompt was assigned",
    CHAPTER_ASSIGNED: "A story section was assigned",
  };
  return labels[activity.eventType];
}

export function relativeActivityTime(value: string, now = Date.now()) {
  const elapsed = Math.max(0, now - Date.parse(value));
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(
    new Date(value),
  );
}
